/**
 * 通用「尺寸」设置：宽高与内边距（对应 Element.size）。
 */
import { useState, type ChangeEvent } from 'react';
import { Button, cn, InputGroup } from '@heroui/react';
import { IconBoxPadding, IconMinus } from '@tabler/icons-react';
import type { BoxSpacing, Element, ElementSize } from '@/components/canvas/document/publicTypes';

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function isUniformPadding(p: BoxSpacing): boolean {
  return p.top === p.right && p.right === p.bottom && p.bottom === p.left;
}

function uniformPaddingValue(p: BoxSpacing | undefined): number {
  if (!p) return 0;
  return p.top;
}

function zeroPadding(): BoxSpacing {
  return { top: 0, right: 0, bottom: 0, left: 0 };
}

function resolvePadding(p: BoxSpacing | undefined): BoxSpacing {
  return p ?? zeroPadding();
}

export type BoxSizeSettingsValue = Pick<
  ElementSize,
  | 'width'
  | 'height'
  | 'minWidth'
  | 'minHeight'
  | 'maxWidth'
  | 'maxHeight'
  | 'fit'
  | 'overflow'
  | 'scrollX'
  | 'scrollY'
  | 'padding'
>;

export interface BoxSizeSettingsProps {
  className?: string;
  title?: string;
  value: BoxSizeSettingsValue;
  onChange: (patch: Partial<BoxSizeSettingsValue>) => void;
}

/** 从元素读出尺寸面板值 */
export function boxSizeSettingsValueFromElement(
  element: Pick<Element, 'size'>,
): BoxSizeSettingsValue {
  return {
    width: element.size.width,
    height: element.size.height,
    minWidth: element.size.minWidth,
    minHeight: element.size.minHeight,
    maxWidth: element.size.maxWidth,
    maxHeight: element.size.maxHeight,
    fit: element.size.fit,
    overflow: element.size.overflow,
    scrollX: element.size.scrollX,
    scrollY: element.size.scrollY,
    padding: element.size.padding,
  };
}

type PaddingSide = keyof BoxSpacing;

function PaddingSideInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (next: number) => void;
}) {
  return (
    <div className="min-w-0 flex-1">
      <InputGroup aria-label={label} variant="secondary">
        <InputGroup.Prefix>{label}</InputGroup.Prefix>
        <InputGroup.Input
          type="number"
          min={0}
          step={1}
          value={value}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onChange(Math.max(0, Math.round(num(e.target.value))))
          }
          className="w-full text-center"
        />
      </InputGroup>
    </div>
  );
}

export function BoxSizeSettings({
  className,
  title = '尺寸',
  value,
  onChange,
}: BoxSizeSettingsProps) {
  const padding = value.padding;
  const resolved = resolvePadding(padding);

  const [paddingExpanded, setPaddingExpanded] = useState(() => {
    if (!padding) return false;
    return !isUniformPadding(padding);
  });

  const patchPadding = (next: BoxSpacing | undefined) => {
    if (next == null || (next.top === 0 && next.right === 0 && next.bottom === 0 && next.left === 0)) {
      onChange({ padding: undefined });
      setPaddingExpanded(false);
      return;
    }
    onChange({ padding: next });
  };

  const patchPaddingSide = (side: PaddingSide, n: number) => {
    patchPadding({ ...resolved, [side]: n });
  };

  const setUniformPadding = (n: number) => {
    const v = Math.max(0, Math.round(n));
    patchPadding({ top: v, right: v, bottom: v, left: v });
  };

  const expandPadding = () => {
    if (!padding || isUniformPadding(padding)) {
      const v = uniformPaddingValue(padding);
      patchPadding({ top: v, right: v, bottom: v, left: v });
    }
    setPaddingExpanded(true);
  };

  const collapsePadding = () => {
    patchPadding(undefined);
  };

  return (
    <div className={cn('flex w-full flex-col gap-2', className)}>
      <p className="text-sm font-medium">{title}</p>

      <div className="flex flex-row gap-1">
        <InputGroup aria-label="元素宽度" variant="secondary" className="min-w-0 flex-1">
          <InputGroup.Prefix>宽</InputGroup.Prefix>
          <InputGroup.Input
            type="number"
            min={1}
            step={1}
            value={Math.max(1, Math.round(value.width))}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onChange({ width: Math.max(1, Math.round(num(e.target.value))) })
            }
          />
        </InputGroup>
        <InputGroup aria-label="元素高度" variant="secondary" className="min-w-0 flex-1">
          <InputGroup.Prefix>高</InputGroup.Prefix>
          <InputGroup.Input
            type="number"
            min={1}
            step={1}
            value={Math.max(1, Math.round(value.height))}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onChange({ height: Math.max(1, Math.round(num(e.target.value))) })
            }
          />
        </InputGroup>
      </div>

      {!paddingExpanded ? (
        <div className="flex items-center gap-1">
          <InputGroup aria-label="内边距" variant="secondary" className="min-w-0 flex-1">
            <InputGroup.Prefix className="text-nowrap">内边距</InputGroup.Prefix>
            <InputGroup.Input
              type="number"
              min={0}
              step={1}
              value={uniformPaddingValue(padding)}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setUniformPadding(num(e.target.value))
              }
            />
          </InputGroup>
          <Button
            variant="ghost"
            isIconOnly
            size="sm"
            aria-label="分别设置四边内边距"
            onMouseDown={(e) => e.preventDefault()}
            onPress={expandPadding}
          >
            <IconBoxPadding stroke={1.5} />
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-[1fr_1fr_auto] grid-rows-2 gap-1">
          <PaddingSideInput
            label="上边距"
            value={resolved.top}
            onChange={(n) => patchPaddingSide('top', n)}
          />
          <PaddingSideInput
            label="下边距"
            value={resolved.bottom}
            onChange={(n) => patchPaddingSide('bottom', n)}
          />
          <div className="row-span-2 self-center">
            <Button
              variant="ghost"
              isIconOnly
              size="sm"
              aria-label="收起分边内边距"
              onMouseDown={(e) => e.preventDefault()}
              onPress={collapsePadding}
            >
              <IconMinus stroke={1.5} />
            </Button>
          </div>
          <PaddingSideInput
            label="左边距"
            value={resolved.left}
            onChange={(n) => patchPaddingSide('left', n)}
          />
          <PaddingSideInput
            label="右边距"
            value={resolved.right}
            onChange={(n) => patchPaddingSide('right', n)}
          />
        </div>
      )}
    </div>
  );
}
